import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {SearchComponent} from "../../shared/components/search/search.component";
import {FiltersTab} from "../../shared/models/common.interface";
import {PageParams} from "../../shared/models/quotes.interface";
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef, MatNoDataRow, MatRow, MatRowDef, MatTable,
  MatTableDataSource
} from "@angular/material/table";
import {finalize, Subject, take, takeUntil} from "rxjs";
import {getHttpParams} from "../../shared/utils/create-http-params";
import {BILLS_TABLE_COLUMNS, DEFAULT_PAGE_PARAMS, FILTER_TABS_CONFIG} from "./bills-dashboard.config";
import {BillsRepository} from "../../shared/repositories/bills.repository";
import {BillsGridResponse, CensusFileUploadSummary, IBill} from "../../shared/models/bills.interface";
import {FullWidthScrollDirective} from "../../shared/directives/full-width-scroll.directive";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatTabLink, MatTabNav, MatTabNavPanel} from "@angular/material/tabs";
import {ModalDialogComponent} from "../../shared/components/modal-dialog/modal-dialog.component";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {GlobalMessagesService} from "../../shared/services/global-messages.service";
import {QuotesRepository} from "../../shared/repositories/quotes.repository";
import {ShareQuoteDialogComponent} from "../../shared/components/share-quote-dialog/share-quote-dialog.component";
import {UploadDialogComponent} from "../../shared/components/upload-dialog/upload-dialog.component";
import {ModalDialogData} from "../../shared/models/modal-dialog.interface";
import {routesPaths} from "../../app.routes";

@Component({
  selector: 'app-bills-dashboard',
  standalone: true,
  imports: [
    FullWidthScrollDirective,
    MatButton,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatPaginator,
    MatRow,
    MatRowDef,
    MatTabLink,
    MatTabNav,
    MatTabNavPanel,
    MatTable,
    ModalDialogComponent,
    SearchComponent,
    MatMenuTrigger,
    MatHeaderCellDef,
    MatNoDataRow,
    ShareQuoteDialogComponent,
    UploadDialogComponent
  ],
  templateUrl: './bills-dashboard.component.html',
  styleUrl: './bills-dashboard.component.scss'
})
export class BillsDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('search') searchComponent: SearchComponent;
  @ViewChild('censusUploadSummaryDialog') censusUploadSummaryDialog: TemplateRef<any>;
  @ViewChild('enrolmentDialogTemplate') enrolmentDialogTemplate: TemplateRef<any>;

  censusuploadSummaryDialogRef: MatDialogRef<ModalDialogComponent>;
  enrolmentDialogRef: MatDialogRef<any>;
  currentBillForEnrolment: IBill | null = null;

  filterTabs = FILTER_TABS_CONFIG;
  activeTab: FiltersTab = this.filterTabs[0];
  pageParams: PageParams = DEFAULT_PAGE_PARAMS;
  displayedColumns = BILLS_TABLE_COLUMNS;
  pageSizeOptions = [10, 25, 50];
  billsTotalCount: number;
  listBillPdfTemplateLink: string =
    'https://flexbenefitstorage.blob.core.windows.net/prod/packageBrochures/ListBillAgreement-FlexBenefits.pdf';
  censusTemplateSampleLink: string = 'https://flexbenefitstorage.blob.core.windows.net/prod/census_template.csv';
  data: IBill[] = [];
  dataSource = new MatTableDataSource(this.data);
  isInProgress: boolean = false;
  searchValue: string = '';
  isShowShareLinkDialog: boolean = false;
  shareLink: string;
  shareLinkCurrentValue: string;
  listBillIdCurrentValue: string;
  showUploadCensusModal: boolean = false;
  groupIdForCensusFileUpload: string = '';
  censusUploadSummary: CensusFileUploadSummary;
  destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private billsRepository: BillsRepository,
    private dialog: MatDialog,
    private messageService: GlobalMessagesService,
    private quotesRepository: QuotesRepository
  ) {}

  ngOnInit() {
    this.setFilter(this.activeTab);
    this.setData(this.pageParams);
    this.setSharedLink();
    if (history.state?.success) {
      this.messageService.show('success', `Group was ${history.state.action}.`);
    }
  }

  goBack(): void {
    this.router.navigate(['dashboard']);
  }

  ngAfterViewInit() {
    this.paginator.selectConfig.panelClass = 'select-dropdown-paginator';
  }

  onSearched(value: string | null): void {
    this.searchValue = value;
    this.pageParams = {
      ...this.pageParams,
      PageIndex: 0,
      ByName: value,
    };
    this.setData(this.pageParams);
  }

  setData(pageParams: PageParams): void {
    const params = getHttpParams(pageParams);
    this.isInProgress = true;
    this.billsRepository
      .getBillsData(params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isInProgress = false))
      )
      .subscribe((res: BillsGridResponse) => {
        this.billsTotalCount = res.total;
        this.dataSource = new MatTableDataSource(res.data);
      });
  }

  applyFilter(currentTab: FiltersTab): void {
    this.activeTab = currentTab;
    this.setFilter(currentTab);
    this.setData(this.pageParams);
  }

  setFilter(tab: FiltersTab): void {
    if (tab.filterType) {
      this.pageParams = {
        ...DEFAULT_PAGE_PARAMS,
        PageSize: this.pageParams.PageSize,
        ByName: this.searchValue,
      };
    } else {
      this.pageParams = {
        ...DEFAULT_PAGE_PARAMS,
        PageSize: this.pageParams.PageSize,
        ByName: this.searchValue,
      };
    }
  }

  createBill() {
    this.router.navigate([routesPaths.billsDashboard, 'create']);
  }

  editBill(bill: IBill) {
    this.router.navigate([routesPaths.billsDashboard, 'edit', bill.id]);
  }

  createQuote(bill: IBill) {
    const url = `${this.shareLink}&ListBill=${bill.id}&isAgentMode=true`;
    window.open(url, '_blank');
  }

  shareLinkClick(bill: IBill) {
    this.shareLinkCurrentValue = `${this.shareLink}&ListBill=${bill.id}`;
    this.listBillIdCurrentValue = `${bill.id}`;
    this.isShowShareLinkDialog = !this.isShowShareLinkDialog;
  }

  downloadGroupCensusClick(bill: IBill): void {
    this.billsRepository.getGroupCensusReport(bill.id).subscribe(res => {
      const contenttype = res.headers.get('Content-Type');
      const datepipe: DatePipe = new DatePipe('en-US');
      const formattedDate = datepipe.transform(new Date(), 'MMddYYYY');
      this.downloadFile(res.body, bill.name + '_' + formattedDate, contenttype);
    });
  }

  uploadCensusFileClick(bill: IBill) {
    this.groupIdForCensusFileUpload = bill.id;
    this.showUploadCensusModal = true;
  }

  sendAllEnrollmentClick(bill: IBill): void {
    this.currentBillForEnrolment = bill;
    this.enrolmentDialogRef = this.dialog.open(this.enrolmentDialogTemplate, {
      data: { title: 'Send all enrolment', hasContent: true, hideActions: true } as ModalDialogData,
      width: '450px',
      autoFocus: false,
    });
  }

  closeEnrolmentDialog(): void {
    this.enrolmentDialogRef?.close();
    this.currentBillForEnrolment = null;
  }

  sendAllEnrolmentByMail(): void {
    this.billsRepository.sendAllEnrolmentByMail(this.currentBillForEnrolment.id).subscribe({
      next: () => this.closeEnrolmentDialog(),
      error: (err) => console.log(err),
    });
  }

  onCensusFileUpload(file) {
    this.billsRepository.uploadCensusFile(file, this.groupIdForCensusFileUpload).subscribe((res) => {
      this.censusUploadSummary = res;
      this.showCensusUploadSummaryDialog();
    });
  }

  showCensusUploadSummaryDialog() {
    const confirmationModalData: ModalDialogData = {
      id: 'confirmationModalData',
      title: 'Uploading Summary',
      cancelActionText: 'Close',
      showCancelBtn: true,
      hasContent: true,
    };
    this.censusuploadSummaryDialogRef = this.dialog.open(this.censusUploadSummaryDialog, {
      data: confirmationModalData,
      width: '550px',
      autoFocus: false,
      disableClose: true,
    });
  }

  hideCensusUploadModal() {
    this.showUploadCensusModal = false;
  }

  downloadFile(body: string, fileName: string, type = 'text/csv', extension = '.csv'): void {
    const blob = new Blob([body], { type });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName.endsWith(extension) ? fileName : `${fileName}${extension}`}`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onPaginatorChange($event: PageEvent): void {
    this.pageParams.PageIndex = $event.pageIndex;
    this.pageParams.PageSize = $event.pageSize;
    this.setData(this.pageParams);
  }

  setSharedLink(): void {
    this.quotesRepository
      .getSharedLink()
      .pipe(take(1))
      .subscribe(link => {
        this.shareLink = link;
      });
  }

  closeShareQuoteDialog(): void {
    this.isShowShareLinkDialog = false;
  }

  shareLinkOverlayClick(event) {
    event.stopPropagation();
    if (event.target.classList.contains('share-link-dialog-container')) {
      this.closeShareQuoteDialog();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
